import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { AutoComplete, Button, Checkbox, ColumnsType, Input, Select, Space, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionFormState } from '@hooks/useSessionFormState';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import useAppointmentPrescriptions from '@hooks/useAppointmentPrescriptions';
import assert from 'assert';
import type { SessionForm } from '@components/Clinicians/session-form';
import useMedicationsLoader from '@hooks/useMedicationsLoader';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';
import { useDebounce } from '@hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { useDPACheck } from '@hooks/useDPACheck';
import { useDebouncedCallback } from '@mantine/hooks';
import { SessionFormItem } from './SessionFormItem';

const { Option } = Select;

type SessionMedication = NonNullable<SessionForm['tableMedications']>;
type MedicationInputField = keyof Pick<SessionMedication[number], 'frequency' | 'duration'>;
type MedicationInformation = {
  medicationUrl: string | undefined;
  medicationName: string;
};

export const MedicationsFormItem = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [selectedMedication, setSelectedMedication] = useState<
    | {
        // vtm_id is returned as integer from BE but FE casts it as a string
        // This is because the integer can be too large for JS integers
        // So vtm_id must be a string everywhere it's referenced on FE
        vtm_id: string;
        vtm_name: string;
        bnf_url: string | null;
      }
    | undefined
  >();
  const [autoCompleteField, setAutoCompleteField] = useState('');

  const [filteredMedications, setFilteredMedications] = useSessionFormState('filteredMedications');
  const [tableMedications, setTableMedications] = useSessionFormState('tableMedications', []);
  const tableMedicationsSnapshot = useRef<SessionForm['tableMedications']>([]);
  const [, setAlert] = useSessionFormState('alert');

  const { canPrescribe, prescribeMedication, updatePrescribeMedication } = useAppointmentPrescriptions();

  const appt_id = useSessionAppointmentId();
  const { appointment } = useSessionAppointment();

  const { isLoading: isLoadingAppointmentDetails, refetch: refetchAppointmentDetails } = useAppointmentDetails(appt_id);

  const { maybeShowDPACheckConfirmationModal } = useDPACheck();

  const debouncedRefetch = useDebouncedCallback(async () => {
    await refetchAppointmentDetails();
  }, 1500);

  const isCompleted = !!appointment?.appointment_completed;
  const readonly = !!appointment?.read_only;

  assert(appointment?.clinician_user_guid, 'clinician_user_guid is required');

  const medicationsLoader = useMedicationsLoader(appointment?.clinician_user_guid, appt_id);

  const autoCompleteId = useId();

  // Odd, but using useQuery to get reliable a loading state from medicationsLoader
  // before putting the results into form state
  const { isLoading: isLoadingTableMedication } = useQuery({
    queryKey: ['medicationsLoader', appointment.appointment_id, appointment?.appointment_completed],
    queryFn: async () => {
      const { medications } = await medicationsLoader();
      setTableMedications(medications);
      tableMedicationsSnapshot.current = medications;

      return null;
    },
    // wait for appointment details to be loaded
    // otherwise medicationsLoader returns an empty array
    enabled: !isLoadingAppointmentDetails,
    gcTime: Infinity,
    staleTime: Infinity
  });

  const saveMeds = useCallback(
    async (meds: Parameters<typeof actions.createSavedMedication>[0]) => {
      const res = await dispatch(actions.createSavedMedication(meds), false);
      debouncedRefetch();
      if (res instanceof Response) {
        setAlert({ type: 'error', message: res.statusText });
      }

      return res;
    },
    [setAlert, dispatch]
  );

  const onEmptyTableMedication = () => {
    updatePrescribeMedication(false);
  };

  const onRemoveTableMedication = (index: number) => {
    setTableMedications((prev) => {
      const newMedicationList = prev?.filter((_, i) => index !== i);

      if (!newMedicationList?.length) {
        onEmptyTableMedication();
      }

      return newMedicationList;
    });
  };

  const onUpdateTableMedications = (newMedicationList: NonNullable<SessionForm['tableMedications']>) => {
    if (!newMedicationList?.length) {
      onEmptyTableMedication();
    }
    setTableMedications(newMedicationList);
  };

  const MAX_FIELD_LENGTH = 255;
  const getFieldChangeHandler =
    (field: MedicationInputField, index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!Array.isArray(tableMedications)) return;

      const value = e.target.value.slice(0, MAX_FIELD_LENGTH);

      const updatedTable = tableMedications.map((row, i) => (i === index ? { ...row, [field]: value } : row));

      onUpdateTableMedications(updatedTable);
      setFilteredMedications([]);
    };

  const getFieldBlurHandler = (field: MedicationInputField, index: number) => async () => {
    const originalRow = tableMedicationsSnapshot.current?.[index];
    const updatedRow = tableMedications?.[index];

    if (updatedRow?.[field] === originalRow?.[field]) return;

    const payload = {
      p_user_guid: appointment?.clinician_user_guid,
      appt_id,
      vmp_id: updatedRow?.current_vmp?.vmp_id.toString(),
      frequency: updatedRow?.frequency || '',
      duration: updatedRow?.duration || '',
      med_id: updatedRow?.med_id.toString()
    };

    await saveMeds(payload);
    tableMedicationsSnapshot.current = tableMedications;
  };

  const MedicationLink = ({ medicationUrl, medicationName }: MedicationInformation) => {
    if (!medicationUrl) {
      return <p style={{ margin: 'auto', color: 'var(--productGray)' }}>{medicationName}</p>;
    }
    return (
      <a href={medicationUrl} target="_blank" rel="noreferrer">
        {medicationName}
      </a>
    );
  };

  const columns = useMemo<ColumnsType<SessionMedication[number]>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'value',
        render: (text, row, index) => (
          <MedicationLink medicationUrl={tableMedications?.[index].bnf_url || undefined} medicationName={text} />
        )
      },
      {
        title: 'Form',
        dataIndex: 'current_vmp',
        render: (v, row, index) => (
          <Select
            style={{ minWidth: '200px' }}
            onSelect={async (val) => {
              if (!Array.isArray(tableMedications)) return;

              const tempTableMedications = [...tableMedications];
              [tempTableMedications[index].current_vmp] = tableMedications[index].vmp_options.filter(
                (option) => option.vmp_name === val
              );
              const tempObject = {
                p_user_guid: appointment?.clinician_user_guid,
                appt_id,
                vmp_id: tempTableMedications[index].current_vmp?.vmp_id.toString(),
                frequency: row.frequency || '',
                duration: row.duration || '',
                med_id: row.med_id.toString()
              };
              await saveMeds(tempObject);
              onUpdateTableMedications(tempTableMedications);
              setFilteredMedications([]);
            }}
            disabled={isCompleted || readonly}
            value={tableMedications?.[index].current_vmp?.vmp_name}
            data-testid="vmp-select"
            aria-label={`${t('Form for')} ${tableMedications?.[index].vtm_name}`}
          >
            {tableMedications?.[index].vmp_options.map((opt) => (
              <Option key={opt.vmp_name} value={opt.vmp_name}>
                {opt.vmp_name}
              </Option>
            ))}
          </Select>
        )
      },
      {
        title: 'Frequency',
        dataIndex: 'frequency',
        render: (text, row, index) => (
          <Input
            disabled={isCompleted || readonly}
            onChange={getFieldChangeHandler('frequency', index)}
            value={text === ' ' ? '' : text}
            autoComplete="off"
            onBlur={getFieldBlurHandler('frequency', index)}
            placeholder={t('e.g. 2 times a week')}
            maxLength={MAX_FIELD_LENGTH}
            aria-label={`${t('Frequency for')} ${tableMedications?.[index].vtm_name}`}
          />
        )
      },
      {
        title: 'Duration',
        colSpan: 1,
        dataIndex: 'duration',
        render: (text, row, index) => (
          <Input
            disabled={isCompleted || readonly}
            onChange={getFieldChangeHandler('duration', index)}
            onBlur={getFieldBlurHandler('duration', index)}
            value={text === ' ' ? '' : text}
            autoComplete="off"
            placeholder={t('e.g. 3 months')}
            maxLength={MAX_FIELD_LENGTH}
            aria-label={`${t('Duration for')} ${tableMedications?.[index].vtm_name}`}
          />
        )
      },
      {
        title: 'Delete',
        dataIndex: 'delete',
        render: (text, row, index) => (
          <UnstyledButton
            disabled={isCompleted || readonly}
            type="button"
            data-testid="remove-medication"
            onClick={async () => {
              if (!Array.isArray(tableMedications)) return;

              if (row.med_id) {
                const tempObject = {
                  p_user_guid: appointment?.clinician_user_guid,
                  appt_id,
                  med_id: row.med_id.toString()
                };
                await dispatch(actions.disableSavedMedication(tempObject));
              }

              debouncedRefetch();
              onRemoveTableMedication(index);
            }}
          >
            <RiDeleteBin5Line aria-label={`${t('Delete')} ${tableMedications?.[index].vtm_name}`} />
          </UnstyledButton>
        )
      }
    ],
    [
      appt_id,
      appointment?.clinician_user_guid,
      dispatch,
      setFilteredMedications,
      setTableMedications,
      t,
      tableMedications,
      canPrescribe,
      prescribeMedication,
      saveMeds,
      isCompleted,
      readonly
    ]
  );

  return (
    <SessionFormItem label={t('Medication')} htmlFor={autoCompleteId}>
      <Space.Compact style={{ width: '100%' }}>
        <AutoComplete
          allowClear
          id={autoCompleteId}
          style={{ width: 'calc(100% - 62px)' }}
          options={filteredMedications}
          onSelect={(e, value) => {
            setSelectedMedication(value);
          }}
          onChange={(value) => setAutoCompleteField(value)}
          onSearch={useDebounce(async (value) => {
            assert(appointment?.clinician_user_guid, 'clinician_user_guid is required');

            if (value.length > 3) {
              const getDrugsByName = await dispatch(
                actions.getDrugsByName(appointment?.clinician_user_guid, appt_id, value.toLowerCase())
              );
              const { vtm } = getDrugsByName;
              if (vtm) {
                setFilteredMedications(
                  vtm.map((element) => ({
                    ...element,
                    value: element.vtm_name
                  }))
                );
              } else {
                setFilteredMedications([]);
              }
            } else {
              setFilteredMedications([]);
            }
          }, 500)}
          value={autoCompleteField}
          placeholder={t('Type to search for medication')}
          disabled={isCompleted || readonly}
          aria-label={t('Medication')}
        />
        <Button
          type="default"
          disabled={isCompleted || readonly}
          style={{ width: '62px' }}
          onClick={async () => {
            if (!selectedMedication || Object.keys(selectedMedication).length < 1 || !appointment?.clinician_user_guid)
              return;

            const vmp = await dispatch(
              actions.getDrugForms(appointment?.clinician_user_guid, appt_id, String(selectedMedication.vtm_id))
            );
            const tempSelected: SessionMedication[number] = {
              ...selectedMedication,
              vmp_options: vmp.vmp,
              current_vmp: vmp.vmp[0],
              med_id: 0,
              frequency: '',
              duration: '',
              vmp_name: vmp.vmp[0].vmp_name,
              vmp_id: vmp.vmp[0].vmp_id
            };

            const tempObject = {
              p_user_guid: appointment?.clinician_user_guid,
              appt_id,
              vmp_id: tempSelected.current_vmp?.vmp_id,
              frequency: '',
              duration: ''
            };
            const response = await saveMeds(tempObject);
            setSelectedMedication(undefined);
            setAutoCompleteField('');
            setFilteredMedications([]);
            if (response.med_id && Array.isArray(tableMedications)) {
              tempSelected.med_id = response.med_id;
              const newTableMedications = [...tableMedications, tempSelected];
              onUpdateTableMedications(newTableMedications);
              tableMedicationsSnapshot.current = newTableMedications;
            }
          }}
          data-testid="add-medication"
        >
          {t('Add')}
        </Button>
      </Space.Compact>

      <Table
        columns={columns}
        dataSource={tableMedications}
        loading={isLoadingTableMedication}
        style={{
          marginTop: '20px',
          overflowX: 'auto'
        }}
        data-testid="medications-table"
      />
      {canPrescribe && (
        <Checkbox
          checked={prescribeMedication}
          onChange={() => {
            if (!prescribeMedication) {
              maybeShowDPACheckConfirmationModal({
                action: 'prescribe medication',
                onOk: () => updatePrescribeMedication(!prescribeMedication)
              });
            } else {
              updatePrescribeMedication(!prescribeMedication);
            }
          }}
          disabled={Number(tableMedications?.length) < 1 || isCompleted || readonly}
        >
          {t('Prescribe using Signature Pharmacy')}
        </Checkbox>
      )}
    </SessionFormItem>
  );
};
